import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner, Alert, Table, ProgressBar } from 'react-bootstrap';
import { useHistory } from "react-router-dom";
import { constants, generateRoutePath } from 'utils';
import { useAuthUserData } from "hooks/useAuthUserData/useAuthUserData";
import { request } from "hooks/useOkoApi/useOkoApi";

export const SessionReplay = () => {
  const [sessionId, setSessionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [compilationStatus, setCompilationStatus] = useState(null);
  const [statusCheckInterval, setStatusCheckInterval] = useState(null);
  const [compilationError, setCompilationError] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);
  const [compilationProgress, setCompilationProgress] = useState(0);
  const [hasLoadedRecentSessions, setHasLoadedRecentSessions] = useState(false);
  const { authUserData, loading: userLoading } = useAuthUserData();
  const history = useHistory();
  const compilationStatusRef = useRef(compilationStatus);

  const isValidUUID = (str) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  };

  useEffect(() => {
    compilationStatusRef.current = compilationStatus;
  }, [compilationStatus]);

  useEffect(() => {
    return () => {
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
      }
    };
  }, [statusCheckInterval]);

  useEffect(() => {
    const fetchRecentSessions = async () => {
      if (!userLoading && authUserData && !authUserData.isAnonymous && !hasLoadedRecentSessions) {
        try {
          setHasLoadedRecentSessions(true);
          const data = await request(`session_replay/recent_sessions?num_sessions=8`, {
            method: 'GET'
          });
          setRecentSessions(data.sessions || []);
        } catch (err) {
          console.error('Failed to fetch recent sessions:', err);
          setHasLoadedRecentSessions(false);
        }
      }
    };
    
    if (!userLoading && authUserData && !authUserData.isAnonymous && !hasLoadedRecentSessions) {
      fetchRecentSessions();
    }
  }, [userLoading, authUserData, hasLoadedRecentSessions]);

  if (userLoading) return <></>;
  if (authUserData && authUserData.isAnonymous) {
    history.push(generateRoutePath(constants.ADMIN_LOGIN_ROUTE_NAME));
    return <></>;
  }

  const checkSessionStatus = async () => {
    if (!sessionId) return;
    
    if (compilationStatusRef.current === 'completed' || compilationStatusRef.current === 'failed') {
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
        setStatusCheckInterval(null);
      }
      return;
    }
    
    try {
      const data = await request(`session_replay/${sessionId}/status`, {
        method: 'GET'
      });

      setCompilationStatus(data.status);
      
      if (data.status === 'pending') {
        setCompilationProgress(10);
      } else if (data.status === 'processing') {
        setCompilationProgress(prev => Math.min(prev + 5, 90));
      }
      if (data.status === 'completed' || data.status === 'failed') {
        if (statusCheckInterval) {
          clearInterval(statusCheckInterval);
          setStatusCheckInterval(null);
        }
        if (data.status === 'failed') {
          setCompilationError(data.error_message || 'Unknown error occurred during compilation');
          setCompilationProgress(0);
        } else if (data.status === 'completed') {
          setCompilationError(null);
          setCompilationProgress(100);
        }
      }
    } catch (err) {
      console.error('Error checking status:', err);
      if (err.detail) {
        setError('Session not found');
        setCompilationStatus(null);
        if (statusCheckInterval) {
          clearInterval(statusCheckInterval);
          setStatusCheckInterval(null);
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!sessionId.trim()) return;
    
    setError(null);
    setCompilationError(null);
    setCompilationProgress(0);
    setLoading(true);
    
    try {
      try {
        const statusData = await request(`session_replay/${sessionId}/status`, {
          method: 'GET'
        });

        setCompilationStatus(statusData.status);

        if (statusData.status === 'completed') {
          setLoading(false);
          return;
        }

        if (statusData.status === 'failed') {
          setCompilationError(statusData.error_message || 'Unknown error occurred during compilation');
          setLoading(false);
          return;
        }

        if (statusData.status === 'processing' || statusData.status === 'pending') {
          const interval = setInterval(checkSessionStatus, 3000);
          setStatusCheckInterval(interval);
          checkSessionStatus();
        }
      } catch (statusErr) {
        console.log('Status check error:', statusErr);
        // Check if it's a "not found" error - the error object is just the parsed JSON response
        const isNotFound = (statusErr.detail && typeof statusErr.detail === 'string' && statusErr.detail.toLowerCase().includes('no compilation found')) ||
                          (statusErr.detail && typeof statusErr.detail === 'string' && statusErr.detail.toLowerCase().includes('not found'));
        
        if (isNotFound) {
          console.log('Session not found in PostgreSQL, checking BigQuery...');
          try {
            await request(`session_replay/${sessionId}/compile`, {
              method: 'POST',
              payload: {}
            });
            console.log('Compile started for session:', sessionId);

            setCompilationStatus('processing');
            setCompilationProgress(10);

            const interval = setInterval(checkSessionStatus, 3000);
            setStatusCheckInterval(interval);
            setTimeout(checkSessionStatus, 2000);
          } catch (compileErr) {
            console.log('Compile error:', compileErr);
            const isSessionNotFound = (compileErr.detail && typeof compileErr.detail === 'string' && 
                                     compileErr.detail.toLowerCase().includes('not found'));
            
            const isServerError = (compileErr.detail && typeof compileErr.detail === 'string' && 
                                 compileErr.detail.toLowerCase().includes('failed to verify'));
            
            if (isSessionNotFound) {
              console.log('Setting error: Session not found');
              setError('Session not found. Please check the session ID.');
            } else if (isServerError) {
              console.log('Setting error: Server error');
              setError('Unable to access session data. Please try again later.');
            } else {
              console.log('Setting error: Compilation failed');
              setError('Failed to process session. Please try again later.');
            }
          }
        } else {
          throw statusErr;
        }
      }
    } catch (err) {
      console.error('SessionReplay error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSessionClick = (clickedSessionId) => {
    setSessionId(clickedSessionId);
    setCompilationStatus(null);
    setError(null);
    setCompilationError(null);
    setCompilationProgress(0);
    if (statusCheckInterval) {
      clearInterval(statusCheckInterval);
      setStatusCheckInterval(null);
    }
  };

  const handleRetryCompilation = async () => {
    setError(null);
    setCompilationError(null);
    setCompilationProgress(0);
    setLoading(true);
    
    try {
      await request(`session_replay/${sessionId}/compile?retry=true`, {
        method: 'POST',
        payload: {}
      });
      
      setCompilationStatus('processing');
      setCompilationProgress(10);
      
      const interval = setInterval(checkSessionStatus, 3000);
      setStatusCheckInterval(interval);
      setTimeout(checkSessionStatus, 2000);
    } catch (err) {
      setError('Failed to retry compilation. Please try again later.');
      console.error('Retry compilation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const downloadSession = async () => {
    setError(null);
    try {
      const auth = await import('hooks/useFirebase').then(m => m.auth);
      const token = await auth.currentUser.getIdToken();

      const response = await fetch(`${process.env.REACT_APP_OKOLABS_API_ENDPOINT}/session_replay/${sessionId}/export/markdown`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'ngrok-skip-browser-warning': '69420',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download session');
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition && contentDisposition.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : `oko_session_${sessionId}.md`;

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err.detail || err.message || 'Failed to download session');
    }
  };

  const uploadToGoogleDocs = async () => {
    setError(null);
    setLoading(true);
    try {
      const auth = await import('hooks/useFirebase').then(m => m.auth);
      const token = await auth.currentUser.getIdToken();

      const response = await fetch(`${process.env.REACT_APP_OKOLABS_API_ENDPOINT}/session_replay/${sessionId}/export/google-docs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'ngrok-skip-browser-warning': '69420',
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to upload to Google Docs');
      }

      const data = await response.json();

      window.open(data.document_url, '_blank');

      alert(`Successfully uploaded to Google Docs!\n\nDocument: ${data.document_name}\n\nOpening in new tab...`);
    } catch (err) {
      setError(err.detail || err.message || 'Failed to upload to Google Docs');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid>
      <Row className="justify-content-center">
        <Col md={10}>
          <Card className="mb-4">
            <Card.Body>
              <h3 className="text-center mb-4">Session Replay Download</h3>
              
              {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-4">
                  {error}
                </Alert>
              )}

              {/* Recent Sessions Table */}
              {recentSessions.length > 0 && (
                <div className="mb-5">
                  <h5 className="mb-3">Recent Sessions from BigQuery</h5>
                  <Row>
                    <Col md={6}>
                      <Table striped bordered hover size="sm">
                        <thead>
                          <tr>
                            <th>Session ID</th>
                            <th>Last Event</th>
                            <th>Events</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentSessions.slice(0, 4).map((session) => (
                            <tr key={session.session_id}>
                              <td>
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0 text-start"
                                  onClick={() => handleSessionClick(session.session_id)}
                                  style={{ fontSize: '0.85rem' }}
                                >
                                  {session.session_id.substring(0, 8)}...
                                </Button>
                              </td>
                              <td style={{ fontSize: '0.85rem' }}>{formatDateTime(session.last_event_time)}</td>
                              <td style={{ fontSize: '0.85rem' }}>{session.event_count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Col>
                    <Col md={6}>
                      <Table striped bordered hover size="sm">
                        <thead>
                          <tr>
                            <th>Session ID</th>
                            <th>Last Event</th>
                            <th>Events</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentSessions.slice(4, 8).map((session) => (
                            <tr key={session.session_id}>
                              <td>
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0 text-start"
                                  onClick={() => handleSessionClick(session.session_id)}
                                  style={{ fontSize: '0.85rem' }}
                                >
                                  {session.session_id.substring(0, 8)}...
                                </Button>
                              </td>
                              <td style={{ fontSize: '0.85rem' }}>{formatDateTime(session.last_event_time)}</td>
                              <td style={{ fontSize: '0.85rem' }}>{session.event_count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Col>
                  </Row>
                </div>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-4">
                  <Form.Label>Session ID</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter session ID"
                    value={sessionId}
                    onChange={(e) => {
                      setSessionId(e.target.value);
                      setCompilationStatus(null);
                      setError(null);
                      setCompilationError(null);
                      setCompilationProgress(0);
                      if (statusCheckInterval) {
                        clearInterval(statusCheckInterval);
                        setStatusCheckInterval(null);
                      }
                    }}
                    disabled={loading || (compilationStatus === 'processing' || compilationStatus === 'pending')}
                  />
                  <Form.Text className="text-muted">
                    Enter the session ID to download replay data
                  </Form.Text>
                </Form.Group>

                {!compilationStatus && (
                  <div className="text-center">
                    <Button 
                      type="submit" 
                      variant="primary"
                      disabled={loading || !sessionId.trim() || !isValidUUID(sessionId.trim())}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Checking...
                        </>
                      ) : (
                        'Check Session'
                      )}
                    </Button>
                    {sessionId.trim() && !isValidUUID(sessionId.trim()) && (
                      <div className="mt-2">
                        <small className="text-danger">Please enter a valid session UUID</small>
                      </div>
                    )}
                  </div>
                )}
              </Form>

              {compilationStatus === 'processing' || compilationStatus === 'pending' ? (
                <div className="text-center mt-4">
                  <div className="mb-3">
                    <ProgressBar 
                      animated 
                      now={compilationProgress} 
                      label={`${compilationProgress}%`}
                      variant="primary"
                    />
                  </div>
                  <p className="text-muted">Generating session replay data, please wait...</p>
                  <small className="text-muted">This may take a few minutes.</small>
                </div>
              ) : compilationStatus === 'completed' ? (
                <div className="text-center mt-4">
                  <div className="d-flex justify-content-center gap-3 mb-3">
                    <Button
                      variant="success"
                      size="lg"
                      onClick={downloadSession}
                    >
                      Download Markdown
                    </Button>
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={uploadToGoogleDocs}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Uploading...
                        </>
                      ) : (
                        'Upload to Google Docs'
                      )}
                    </Button>
                  </div>
                  <div className="mt-2">
                    <Button
                      variant="link"
                      size="sm"
                      onClick={handleRetryCompilation}
                      disabled={loading}
                    >
                      Regenerate session
                    </Button>
                  </div>
                </div>
              ) : compilationStatus === 'failed' ? (
                <div className="text-center mt-4">
                  {compilationError && (
                    <Alert variant="danger" className="mb-3">
                      <strong>Compilation Failed:</strong> {compilationError}
                    </Alert>
                  )}
                  <p className="text-danger mb-3">
                    Session replay compilation failed. Would you like to retry?
                  </p>
                  <Button 
                    variant="warning" 
                    size="lg"
                    onClick={handleRetryCompilation}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Retrying...
                      </>
                    ) : (
                      'Regenerate Session'
                    )}
                  </Button>
                </div>
              ) : null}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};